import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { useToast } from '../components/ui/Toast';

type BrfStatus = 'CREATED' | 'PREPARED' | 'SHIPPED' | 'CREDIT_RECEIVED';

interface BrfSummary {
  id: string;
  brfNumber: string;
  status: BrfStatus;
  reason: string;
  totalHT: string | null;
  creditAmount: string | null;
  creditNoteRef: string | null;
  createdAt: string;
  shippedAt: string | null;
  supplierName: string;
  lineCount: number;
  totalQty: number;
}

interface BrfDetail extends Omit<BrfSummary, 'lineCount' | 'totalQty'> {
  carrierRef: string | null;
  preparedAt: string | null;
  creditReceivedAt: string | null;
  poNumber: string | null;
  lines: Array<{
    id: string;
    productId: string;
    sku: string;
    name: string;
    brand: string | null;
    quantity: number;
    reason: string | null;
    unitPriceHT: string | null;
  }>;
}

/** The linear SC-10 lifecycle, and the single action that advances it from each state. */
const NEXT_ACTION: Record<BrfStatus, { path: string; labelKey: string } | null> = {
  CREATED: { path: 'prepare', labelKey: 'supplierReturns.action.prepare' },
  PREPARED: { path: 'ship', labelKey: 'supplierReturns.action.ship' },
  SHIPPED: null, // needs the supplier's credit-note reference — its own form below
  CREDIT_RECEIVED: null,
};

const STATUS_TONE: Record<BrfStatus, 'secondary' | 'warning' | 'default' | 'success'> = {
  CREATED: 'secondary',
  PREPARED: 'warning',
  SHIPPED: 'default',
  CREDIT_RECEIVED: 'success',
};

const money = (v: string | null): string => (v == null ? '—' : `${Number(v).toFixed(2)} €`);
const date = (v: string | null): string => (v ? new Date(v).toLocaleDateString() : '—');

/**
 * SC-10 — supplier returns (BRF).
 *
 * The list plus a detail panel that only ever offers the ONE next step of the linear state
 * machine, because the server refuses anything else with a 409 and a UI that offers a button the
 * API will reject is a UI that teaches operators to distrust it.
 *
 * The screen states plainly where the stock moves: nothing leaves at CREATED or PREPARED — the
 * parts are still on our shelf in Zone Q — and shipping is the act that takes them out.
 */
export default function SupplierReturns() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [params, setParams] = useSearchParams();

  const [status, setStatus] = useState<BrfStatus | ''>('');
  const [rows, setRows] = useState<BrfSummary[]>([]);
  const [detail, setDetail] = useState<BrfDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [carrierRef, setCarrierRef] = useState('');
  const [creditRef, setCreditRef] = useState('');
  const [creditAmount, setCreditAmount] = useState('');

  const selectedId = params.get('id');

  const load = useCallback(async (): Promise<void> => {
    try {
      const { data } = await apiClient.get<BrfSummary[]>(
        `/supplier-returns${status ? `?status=${status}` : ''}`
      );
      setRows(data);
    } catch (err) {
      showApiError(err);
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDetail = useCallback(async (id: string): Promise<void> => {
    try {
      const { data } = await apiClient.get<BrfDetail>(`/supplier-returns/${id}`);
      setDetail(data);
      setCreditAmount(data.totalHT ?? '');
    } catch (err) {
      showApiError(err);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (selectedId) void loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  const advance = async (path: string): Promise<void> => {
    if (!detail) return;
    setBusy(true);
    try {
      await apiClient.post(
        `/supplier-returns/${detail.id}/${path}`,
        path === 'ship' ? { carrierRef: carrierRef.trim() || undefined } : {}
      );
      push({
        tone: 'success',
        message: t(`supplierReturns.done.${path}`, { number: detail.brfNumber }),
      });
      await Promise.all([load(), loadDetail(detail.id)]);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const closeWithCredit = async (): Promise<void> => {
    if (!detail || !creditRef.trim()) return;
    setBusy(true);
    try {
      await apiClient.post(`/supplier-returns/${detail.id}/credit-received`, {
        creditNoteRef: creditRef.trim(),
        creditAmount: creditAmount === '' ? undefined : Number(creditAmount),
      });
      push({
        tone: 'success',
        message: t('supplierReturns.done.credit', { number: detail.brfNumber }),
      });
      setCreditRef('');
      await Promise.all([load(), loadDetail(detail.id)]);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const next = detail ? NEXT_ACTION[detail.status] : null;

  return (
    <div>
      <PageHeader
        title={t('supplierReturns.title')}
        description={t('supplierReturns.description')}
        actions={
          <Select value={status} onChange={(e) => setStatus(e.target.value as BrfStatus | '')}>
            <option value="">{t('supplierReturns.allStatuses')}</option>
            {(['CREATED', 'PREPARED', 'SHIPPED', 'CREDIT_RECEIVED'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`supplierReturns.status.${s}`)}
              </option>
            ))}
          </Select>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">{t('supplierReturns.col.number')}</th>
                <th className="px-3 py-2">{t('supplierReturns.col.supplier')}</th>
                <th className="px-3 py-2 text-right">{t('supplierReturns.col.qty')}</th>
                <th className="px-3 py-2 text-right">{t('supplierReturns.col.value')}</th>
                <th className="px-3 py-2">{t('supplierReturns.col.status')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setParams({ id: r.id })}
                  className={`cursor-pointer hover:bg-muted/30 ${r.id === selectedId ? 'bg-muted/50' : ''}`}
                >
                  <td className="px-3 py-2 font-mono">{r.brfNumber}</td>
                  <td className="px-3 py-2">
                    <div>{r.supplierName}</div>
                    <div className="text-xs text-muted-foreground">{date(r.createdAt)}</div>
                  </td>
                  <td className="px-3 py-2 text-right">{r.totalQty}</td>
                  <td className="px-3 py-2 text-right">{money(r.creditAmount ?? r.totalHT)}</td>
                  <td className="px-3 py-2">
                    <Badge variant={STATUS_TONE[r.status]}>
                      {t(`supplierReturns.status.${r.status}`)}
                    </Badge>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">
                    {t('supplierReturns.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {detail && (
          <aside className="space-y-4 rounded-lg border p-4">
            <div>
              <div className="font-mono text-lg font-semibold">{detail.brfNumber}</div>
              <div className="text-sm text-muted-foreground">{detail.supplierName}</div>
              <Badge variant={STATUS_TONE[detail.status]} className="mt-2">
                {t(`supplierReturns.status.${detail.status}`)}
              </Badge>
            </div>

            <p className="text-sm">{detail.reason}</p>

            <ul className="divide-y rounded border text-sm">
              {detail.lines.map((l) => (
                <li key={l.id} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="min-w-0">
                    <span className="block truncate">{l.name}</span>
                    <span className="block text-xs text-muted-foreground">{l.sku}</span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block font-medium">×{l.quantity}</span>
                    <span className="block text-xs text-muted-foreground">
                      {money(l.unitPriceHT)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>

            {/* Where the stock is, said out loud — the whole point of the CREATED/PREPARED split. */}
            <p className="rounded bg-muted/40 p-2 text-xs text-muted-foreground">
              {detail.status === 'SHIPPED' || detail.status === 'CREDIT_RECEIVED'
                ? t('supplierReturns.stockGone')
                : t('supplierReturns.stockStillHere')}
            </p>

            {detail.status === 'PREPARED' && (
              <Input
                value={carrierRef}
                onChange={(e) => setCarrierRef(e.target.value)}
                placeholder={t('supplierReturns.carrierRef')}
              />
            )}

            {next && (
              <Button className="w-full" disabled={busy} onClick={() => void advance(next.path)}>
                {t(next.labelKey)}
              </Button>
            )}

            {detail.status === 'SHIPPED' && (
              <div className="space-y-2 rounded border p-3">
                <p className="text-xs font-medium">{t('supplierReturns.recordCredit')}</p>
                <Input
                  value={creditRef}
                  onChange={(e) => setCreditRef(e.target.value)}
                  placeholder={t('supplierReturns.creditNoteRef')}
                />
                <Input
                  type="number"
                  step="0.01"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  placeholder={t('supplierReturns.creditAmount')}
                />
                {/* A supplier crediting less than we claimed is a fact somebody has to see. */}
                {creditAmount !== '' &&
                  detail.totalHT != null &&
                  Number(creditAmount) < Number(detail.totalHT) && (
                    <p className="text-xs text-warning">
                      {t('supplierReturns.creditShort', {
                        claimed: money(detail.totalHT),
                        credited: money(creditAmount),
                      })}
                    </p>
                  )}
                <Button
                  className="w-full"
                  disabled={busy || !creditRef.trim()}
                  onClick={() => void closeWithCredit()}
                >
                  {t('supplierReturns.action.close')}
                </Button>
              </div>
            )}

            {detail.status === 'CREDIT_RECEIVED' && (
              <dl className="space-y-1 text-sm">
                <Row
                  label={t('supplierReturns.creditNoteRef')}
                  value={detail.creditNoteRef ?? '—'}
                />
                <Row label={t('supplierReturns.creditAmount')} value={money(detail.creditAmount)} />
                <Row
                  label={t('supplierReturns.col.closedAt')}
                  value={date(detail.creditReceivedAt)}
                />
              </dl>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
